import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import Modal from 'components/_UI/Modal'
import Button from 'components/_UI/Button'
import Loader from 'components/_UI/Loader'
import Text from 'components/_UI/Text'
import Radio from 'components/_UI/Radio'
import ImageUpload from 'components/_UI/ImageUpload'
import { useEvent } from 'context/events/Event'
import certificateApi from 'api/requests/certificate'
import ticketsApi from 'api/requests/tickets'
import CertificateEditor from './CertificateEditor'
import CertificatePreview from './CertificatePreview'
import CertificateSettings from './CertificateSettings'
import colors from 'theme/colors'
import fonts from 'theme/fonts'
import breakpoints from 'theme/breakpoints'
import { Column } from 'styled/alignment/Column'
import { Row } from 'styled/alignment/Row'
import { Award, Info, Layout, ChevronDown, ChevronUp, Eye, RotateCcw } from 'lucide-react'

const DEFAULT_HTML = `<p style="text-align:center;"><br></p>
<p style="text-align:center;"><br></p>
<p style="text-align:center; font-size:14px; color:#C9A84C;">&#9670; &nbsp;&nbsp; &#9670; &nbsp;&nbsp; &#9670;</p>
<p style="text-align:center;"><br></p>
<p style="text-align:center; font-size:28px; color:#151136;"><strong>CERTIFICADO</strong></p>
<p style="text-align:center; font-size:14px; color:#C9A84C;"><strong>DE PARTICIPAÇÃO</strong></p>
<p style="text-align:center;"><br></p>
<p style="text-align:center; font-size:12px; color:#C9A84C;">&#8212;&#8212;&#8212;&#8212;&#8212; &nbsp; &#10022; &nbsp; &#8212;&#8212;&#8212;&#8212;&#8212;</p>
<p style="text-align:center;"><br></p>
<p style="text-align:center; font-size:12px; color:#707070;">Certificamos que</p>
<p style="text-align:center;"><br></p>
<p style="text-align:center; font-size:24px; color:#151136;"><strong><em>*|PARTICIPANTE|*</em></strong></p>
<p style="text-align:center;"><br></p>
<p style="text-align:center; font-size:12px; color:#707070;">participou do evento</p>
<p style="text-align:center;"><br></p>
<p style="text-align:center; font-size:18px; color:#3F6BC6;"><strong>*|EVENTO|*</strong></p>
<p style="text-align:center;"><br></p>
<p style="text-align:center; font-size:12px; color:#707070;">realizado no dia <strong style="color:#1A1A1A;">*|DATA-DO-EVENTO|*</strong></p>
<p style="text-align:center;"><br></p>
<p style="text-align:center;"><br></p>
<p style="text-align:center; font-size:12px; color:#C9A84C;">&#8212;&#8212;&#8212;&#8212;&#8212; &nbsp; &#10022; &nbsp; &#8212;&#8212;&#8212;&#8212;&#8212;</p>
<p style="text-align:center;"><br></p>
<p style="text-align:center; font-size:14px; color:#151136;"><strong>*|ORGANIZADOR|*</strong></p>
<p style="text-align:center; font-size:12px; color:#707070;"><em>Organizador</em></p>`

function CertificateForm({ visible, setVisible, edit = false, onSuccess }) {
    const { event } = useEvent()

    const [loading, setLoading] = useState(false)
    const [layoutType, setLayoutType] = useState(edit?.layout_type || 'default')
    const [backgroundImage, setBackgroundImage] = useState(edit?.background_image || null)
    const [backgroundFile, setBackgroundFile] = useState(null)
    const [contentHtml, setContentHtml] = useState(edit?.content_html || DEFAULT_HTML)
    const [autoAvailable, setAutoAvailable] = useState(edit?.auto_available ?? true)
    const [accessType, setAccessType] = useState(edit?.access_type || 'all_confirmed')
    const [selectedTickets, setSelectedTickets] = useState([])
    const [ticketOptions, setTicketOptions] = useState([])
    const [showInfo, setShowInfo] = useState(false)
    const [showPreview, setShowPreview] = useState(false)

    useEffect(() => {
        fetchTickets()
    }, [])

    useEffect(() => {
        if (edit?.tickets && ticketOptions.length) {
            const preselected = ticketOptions.filter(opt =>
                edit.tickets.includes(opt.value)
            )
            setSelectedTickets(preselected)
        }
    }, [ticketOptions])

    async function fetchTickets() {
        try {
            const { data } = await ticketsApi.getTicketForEvent(event?.slug)
            const options = (data?.data || []).map(t => ({
                label: t.name || t.title,
                value: t.id,
            }))
            setTicketOptions(options)
        } catch (error) {}
    }

    function resetLayout() {
        setContentHtml(DEFAULT_HTML)
        setLayoutType('default')
        setBackgroundImage(null)
        setBackgroundFile(null)
    }

    function createPayload() {
        return {
            layout_type: layoutType,
            content_html: contentHtml,
            auto_available: autoAvailable,
            access_type: accessType,
            tickets: selectedTickets.map(t => t.value),
        }
    }

    async function handleSubmit() {
        setLoading(true)

        try {
            const payload = createPayload()

            if (edit) {
                await certificateApi.update(event?.slug, payload)

                if (backgroundFile && layoutType === 'custom') {
                    const formData = new FormData()
                    formData.append('background', backgroundFile)
                    await certificateApi.uploadBackground(event?.slug, formData)
                }

                onSuccess('editCertificate')
            } else {
                await certificateApi.create(event?.slug, payload)

                if (backgroundFile && layoutType === 'custom') {
                    const formData = new FormData()
                    formData.append('background', backgroundFile)
                    await certificateApi.uploadBackground(event?.slug, formData)
                }

                onSuccess('createCertificate')
            }
        } catch (error) {
        } finally {
            setLoading(false)
        }
    }

    function handleImageUpload(files) {
        if (files?.length) {
            const file = files[0]
            setBackgroundFile(file)
            setBackgroundImage(file.preview || URL.createObjectURL(file))
        }
    }

    function handleImageDelete() {
        setBackgroundFile(null)
        setBackgroundImage(null)
    }

    return (
        <Modal
            isOpen={visible}
            onClose={() => setVisible(false)}
            size='xLarge'
            childrenOverflowY='scroll'
        >
            <FormContainer>
                {/* Title */}
                <SectionHeader>
                    <SectionIcon><Award size={18} color={colors.occasionalPurple} /></SectionIcon>
                    <Text type='bold' color={colors.nightDark} paddingBottom={0}>
                        Configurar certificados
                    </Text>
                </SectionHeader>

                {/* Layout type */}
                <OptionCard>
                    <Text name='small' type='bold' color={colors.nightDark} paddingBottom={0}>
                        Opções de layout
                    </Text>
                    <Row marginTop='12px' gap='20px'>
                        <Radio
                            selected={layoutType === 'default'}
                            label='Modelo Padrão'
                            textSize={fonts.sizes.small}
                            onSelect={() => setLayoutType('default')}
                        />
                        <Radio
                            selected={layoutType === 'custom'}
                            label='Subir imagem de fundo'
                            textSize={fonts.sizes.small}
                            onSelect={() => setLayoutType('custom')}
                        />
                    </Row>
                </OptionCard>

                {layoutType === 'custom' && (
                    <ImageUploadWrapper>
                        <ImageUpload
                            onToggleFile={handleImageUpload}
                            onDeleteFile={handleImageDelete}
                            isEdit={backgroundImage ? { image: backgroundImage } : null}
                            product
                        />
                    </ImageUploadWrapper>
                )}

                {/* Layout editor header */}
                <Divider />

                <LayoutHeader>
                    <LayoutHeaderLeft>
                        <SectionIcon><Layout size={18} color={colors.occasionalPurple} /></SectionIcon>
                        <Text type='bold' color={colors.nightDark} paddingBottom={0}>
                            Configurar layout
                        </Text>
                    </LayoutHeaderLeft>

                    <ActionsRow>
                        <ActionButton type="button" onClick={() => setShowPreview(true)}>
                            <Eye size={14} />
                            Pré-visualizar
                        </ActionButton>
                        <ResetButton type="button" onClick={resetLayout}>
                            <RotateCcw size={14} />
                            Reiniciar layout
                        </ResetButton>
                    </ActionsRow>
                </LayoutHeader>

                {/* Info accordion */}
                <InfoAccordion>
                    <InfoHeader onClick={() => setShowInfo(!showInfo)}>
                        <InfoHeaderLeft>
                            <Info size={16} color={colors.primaryLight} />
                            <Text paddingBottom={0} name='small' color={colors.nightDark}>
                                Personalizando o certificado para cada participante
                            </Text>
                        </InfoHeaderLeft>
                        {showInfo
                            ? <ChevronUp size={18} color={colors.night} />
                            : <ChevronDown size={18} color={colors.night} />
                        }
                    </InfoHeader>

                    {showInfo && (
                        <InfoBody>
                            <Text name='small' color={colors.nightDark} paddingBottom={0}>
                                Usando Tags, o conteúdo será <strong>preenchido automaticamente</strong> com dados informados pelo Participante na compra do ingresso.
                            </Text>

                            <Text name='small' color={colors.nightDark} paddingBottom={0} marginTop='12px'>
                                Ex: A tag <strong>*|PARTICIPANTE|*</strong> será substituído pelo nome do participante
                            </Text>

                            <ImportantLabel>Importante:</ImportantLabel>

                            <InfoList>
                                <li>Você precisa <strong>gerar as Tags pelo botão "TAGS"</strong> durante a edição do Certificado.</li>
                                <li><strong>Não modifique o texto das Tags.</strong> Você pode mudar outras configurações livremente (Fonte, Tamanho, etc).</li>
                                <li><strong>Os certificados serão preenchidos de acordo com os dados informados pelos participantes na compra do ingresso.</strong></li>
                            </InfoList>
                        </InfoBody>
                    )}
                </InfoAccordion>

                {/* Editor + live preview */}
                <CertificateEditor
                    value={contentHtml}
                    onChange={setContentHtml}
                    layoutType={layoutType}
                    backgroundImage={backgroundImage}
                />

                {/* Settings */}
                <CertificateSettings
                    autoAvailable={autoAvailable}
                    setAutoAvailable={setAutoAvailable}
                    accessType={accessType}
                    setAccessType={setAccessType}
                    selectedTickets={selectedTickets}
                    setSelectedTickets={setSelectedTickets}
                    ticketOptions={ticketOptions}
                />

                {/* Submit */}
                {loading ? (
                    <Column marginTop='24px'>
                        <Loader />
                    </Column>
                ) : (
                    <ButtonsRow>
                        <CancelButton type="button" onClick={() => setVisible(false)}>
                            Cancelar
                        </CancelButton>
                        <Button variant='featured' onClick={handleSubmit}>
                            {edit ? 'Salvar alterações' : 'Criar certificado'}
                        </Button>
                    </ButtonsRow>
                )}
            </FormContainer>

            {showPreview && (
                <CertificatePreview
                    visible={showPreview}
                    onClose={() => setShowPreview(false)}
                    contentHtml={contentHtml}
                    layoutType={layoutType}
                    backgroundImage={backgroundImage}
                />
            )}
        </Modal>
    )
}

export default CertificateForm

const FormContainer = styled(Column)`
    width: 100%;
    text-align: left;
`

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 20px;
`

const SectionIcon = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: #F3EAFF;
    flex-shrink: 0;
`

const OptionCard = styled.div`
    background: ${colors.neutralBackground};
    border: 1px solid ${colors.neutralDark};
    border-radius: 10px;
    padding: 16px;
`

const ImageUploadWrapper = styled.div`
    margin-top: 12px;
    max-width: 400px;
`

const Divider = styled.div`
    border-top: 1px solid ${colors.neutralDark};
    margin: 24px 0 0;
    padding-top: 24px;
`

const LayoutHeader = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 16px;

    @media screen and (min-width: ${breakpoints.md}) {
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
    }
`

const LayoutHeaderLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`

const ActionsRow = styled.div`
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
`

const actionButtonBase = `
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all .2s ease;
    border: none;
`

const ActionButton = styled.button`
    ${actionButtonBase}
    background: #F3EAFF;
    color: ${colors.occasionalPurple};

    &:hover { background: #E8D5FF; }
`

const ResetButton = styled.button`
    ${actionButtonBase}
    background: ${colors.neutralBackground};
    color: ${colors.night};
    border: 1px solid ${colors.neutralDark};

    &:hover {
        background: ${colors.neutralDark};
        color: ${colors.nightDark};
    }
`

const InfoAccordion = styled.div`
    border: 1px solid ${colors.neutralDark};
    border-radius: 10px;
    background: ${colors.neutralBackground};
    overflow: hidden;
    margin-bottom: 16px;
`

const InfoHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 14px 16px;
    cursor: pointer;
    user-select: none;
    transition: background .15s ease;

    &:hover { background: rgba(0, 0, 0, 0.015); }
`

const InfoHeaderLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`

const InfoBody = styled.div`
    padding: 0 16px 16px;
`

const ImportantLabel = styled.p`
    color: ${colors.danger};
    font-size: ${fonts.sizes.small};
    font-weight: 700;
    margin-top: 14px;
    margin-bottom: 2px;
`

const InfoList = styled.ul`
    margin: 6px 0 0 20px;
    font-size: ${fonts.sizes.small};
    color: ${colors.nightDark};
    list-style: disc;

    li {
        margin-bottom: 6px;
        line-height: 1.5;
    }
`

const ButtonsRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 28px;

    @media screen and (min-width: ${breakpoints.md}) {
        flex-direction: row;
    }
`

const CancelButton = styled.button`
    ${actionButtonBase}
    background: ${colors.neutralBackground};
    color: ${colors.night};
    border: 1px solid ${colors.neutralDark};
    justify-content: center;
    padding: 10px 24px;

    &:hover {
        background: ${colors.neutralDark};
        color: ${colors.nightDark};
    }
`
